<!-- IMPROVEMENTS
1. Currently, we must loop through columnHeaders(TableDimensions) in order to create ALL table headers. In so doing,
  we must 'ngIf' the last header's datum <tr> and insert the columnLookup <tr> instead. This causes an issue for a table
  with no columns because the 'ngFor' never runs, and no headers are created. In order to patch this, a 'fake' columnHeader has to be made.
  <SOLUTION>
  I believe a better approach would be to make a '<tr mat-row>' OUTSIDE of the columnHeader 'ngFor', specifically to use
    the columnLookup. Then, inside the columnHeader 'ngFor', 'ngif'-out the last header's datum <tr> -->
<table
  *ngIf="!!tabulation"
  aria-label="Tabulation"
  i18n-aria-label
  mat-table
  tabindex="0"
  [dataSource]="tabulation!.dataSource"
  [class.is-ie]="platform.TRIDENT"
>
  <!-- Totals Header Def -->
  <ng-container matColumnDef="row-total">
    <th
      mat-header-cell
      class="total-col"
      [ngClass]="{ 'hide-row-total': !tabulation!.hasRowTotals, 'no-hide-row-total': !tabulation!.dimensions.columns?.length }"
      [attr.rowspan]="(tabulation!.dimensions.columns?.length || 0) * 2 || 1"
      *matHeaderCellDef
    >
      {{ tabulation!.dimensions.columns?.length ? 'TOTAL' : tabulation!.measureColumnHeader }}
    </th>
    <td
      mat-cell
      *matCellDef="let row"
      [ngClass]="{
        'hide-row-total': !tabulation!.hasRowTotals,
        'no-hide-row-total': !tabulation!.dimensions.columns?.length,
        'sub-total-cell': row.subtotal
      }"
      class="count-cell total-col"
    >
      {{ row.countCells['row-total'] | rdsNumber: minFractionDigits:maxFractionDigits:numberFormat }}

      <span *ngIf="percentType === 'COLUMN' || percentType === 'BOTH'" class="percent-container percent-COLUMN">
        <div class="column-percent">
          {{ row.countCells['row-total'] | rdsPctTotal: tabulation!.columnTotals['row-total']:minFractionDigits:maxFractionDigits }}
          <mat-icon class="percent-icon rotate-90" fxLayout="row" fxLayoutAlign="center center">forward</mat-icon>
        </div>
      </span>
    </td>
    <td
      mat-footer-cell
      *matFooterCellDef
      [ngClass]="{ 'hide-row-total': !tabulation!.hasRowTotals, 'no-hide-row-total': !tabulation!.dimensions.columns?.length }"
      class="count-cell total-col"
    >
      {{ tabulation!.columnTotals['row-total'] | rdsNumber: minFractionDigits:maxFractionDigits:numberFormat }}
    </td>
  </ng-container>

  <!-- Row Dimension Headers -->
  <ng-container
    *ngFor="let rowDim of tabulation!.dimensions.rows; let rowIndex = index; let lastRow = last; let firstRowDim = first"
    [matColumnDef]="$any(rowDim.name)"
  >
    <th class="row-dimension-header" mat-header-cell *matHeaderCellDef>
      {{ (variableDisplay | rdsGetVariableCellValue: rowDim) || '' | uppercase }}
    </th>

    <ng-container *matCellDef="let tableRow">
      <ng-container *ngIf="tableRow.subtotal; else notSubTotal">
        <td *ngIf="firstRowDim" class="row-header-cell last-row-header-cell" [attr.colspan]="tableRow.colspan" mat-cell>
          {{ 'SUBTOTAL' }}
        </td>
      </ng-container>
      <ng-template #notSubTotal>
        <td
          *ngIf="tableRow.datumCells[rowIndex]?.first"
          class="row-header-cell"
          [attr.rowspan]="tableRow.datumCells[rowIndex]?.rowspan"
          mat-cell
        >
          {{
            codeDisplay
              | rdsGetDatumValue: tableRow.datumCells[rowIndex]?.datum:[rowDim]:tabulation!.dataSet.classifications:displayPropNames
          }}
        </td>
      </ng-template>
    </ng-container>
    <ng-container *matFooterCellDef>
      <td *ngIf="rowIndex === 0" mat-footer-cell [attr.colspan]="tabulation!.dimensions.rows.length" class="row-header-cell">TOTAL</td>
    </ng-container>
  </ng-container>

  <!-- Column Dimension Headers - 2 Rows => Variable Name Row, Variable Code Values Row-->
  <ng-container *ngFor="let tableDim of tabulation!.columnHeaders; let dimIndex = index; let firstDim = first; let lastDim = last">
    <ng-container *ngFor="let id of tableDim.headerIds; let firstCell = first; let lastCell = last; let evenCol = even">
      <ng-container *ngIf="id !== 'row-total'" [matColumnDef]="id">
        <!-- Row Dimension Filler Cell -->
        <ng-container *ngIf="id === 'first-row-filler'; else dimNames">
          <th
            mat-header-cell
            *matHeaderCellDef
            [attr.colspan]="tabulation!.dimensions.rows.length"
            [attr.rowspan]="tabulation!.columnHeaders.length * 2 - 1"
          ></th>
        </ng-container>

        <ng-template #dimNames>
          <th
            class="col-dimension-header"
            mat-header-cell
            *matHeaderCellDef
            [attr.colspan]="tableDim.nameCellSpan[id]"
            [ngClass]="{ 'first-col': firstCell, 'last-col': lastCell, 'even-col': evenCol }"
          >
            {{ (tableDim.variable ? (variableDisplay | rdsGetVariableCellValue: tableDim.variable) : '') | uppercase }}
          </th>
        </ng-template>
      </ng-container>
    </ng-container>

    <!-- Value Columns -->
    <ng-container *ngIf="!lastDim; else columnLookup">
      <ng-container
        *ngFor="let cellValue of tableDim.datumCellValues; let firstCell = first; let lastCell = last; let evenCell = even"
        [matColumnDef]="cellValue.id"
      >
        <th
          mat-header-cell
          *matHeaderCellDef
          [attr.colspan]="cellValue.subtotal ? 1 : cellValue.cellspan"
          [ngClass]="{ 'first-col': firstCell, 'last-col': lastCell, 'even-col': evenCell, 'sub-total-cell': cellValue.subtotal }"
          [attr.rowspan]="cellValue.subtotal ? cellValue.cellspan : 1"
        >
          {{
            codeDisplay
              | rdsGetHeaderCellValue
                : cellValue
                : tabulation!.columnLookup.length
                : [tableDim.variable]
                : tabulation!.dataSet.classifications
                : displayPropNames
                : tabulation!.measureColumnHeader
          }}
        </th>
        <ng-container *ngIf="cellValue.subtotal">
          <td
            mat-cell
            *matCellDef="let tableRow"
            [ngClass]="{ 'even-col': evenCell, 'last-col': lastCell, 'first-col': firstCell }"
            class="count-cell sub-total-cell"
          >
            {{ tableRow.countCells[cellValue.id] | rdsNumber: minFractionDigits:maxFractionDigits:numberFormat }}

            <ng-container *ngIf="percentType !== 'NONE' && !!tableRow.countCells[cellValue.id]">
              <ng-container
                *ngTemplateOutlet="
                  percentage;
                  context: {
                    cell: tableRow.countCells[cellValue.id],
                    rowTotal: tableRow.countCells['row-total'],
                    columnTotal: tabulation!.columnTotals[cellValue.id]
                  }
                "
              ></ng-container>
            </ng-container>
          </td>

          <td *matFooterCellDef class="count-cell" mat-footer-cell>
            {{ tabulation.columnTotals[cellValue.id] | rdsNumber: minFractionDigits:maxFractionDigits:numberFormat }}
          </td>
        </ng-container>
      </ng-container>
    </ng-container>

    <ng-template #columnLookup>
      <ng-container *ngFor="let datumCell of tabulation!.columnLookup; let evenCol = even; let firstCol = first; let lastCol = last">
        <ng-container *ngIf="!datumCell.subtotal || datumCell.cellspan === 1" [matColumnDef]="datumCell.id">
          <th
            mat-header-cell
            *matHeaderCellDef
            [ngClass]="{ 'first-col': firstCol, 'last-col': lastCol, 'even-col': evenCol, 'sub-total-cell': datumCell.subtotal }"
          >
            {{
              codeDisplay
                | rdsGetHeaderCellValue
                  : datumCell
                  : tabulation!.columnLookup.length
                  : [tableDim.variable]
                  : tabulation!.dataSet.classifications
                  : displayPropNames
                  : tabulation!.measureColumnHeader
            }}
          </th>

          <td
            mat-cell
            *matCellDef="let tableRow"
            [ngClass]="{
              'first-col': firstCol,
              'last-col': lastCol,
              'even-col': evenCol,
              'sub-total-cell': tableRow.subtotal || datumCell.subtotal
            }"
            class="count-cell"
          >
            {{ tableRow.countCells[datumCell.id] | rdsNumber: minFractionDigits:maxFractionDigits:numberFormat }}

            <ng-container *ngIf="percentType !== 'NONE' && !!tableRow.countCells[datumCell.id]">
              <ng-container
                *ngTemplateOutlet="
                  percentage;
                  context: {
                    cell: tableRow.countCells[datumCell.id],
                    rowTotal: tableRow.countCells['row-total'],
                    columnTotal: tabulation!.columnTotals[datumCell.id]
                  }
                "
              ></ng-container>
            </ng-container>
          </td>

          <td
            *matFooterCellDef
            class="count-cell"
            [ngClass]="{ 'first-col': firstCol, 'last-col': lastCol, 'even-col': evenCol, 'sub-total-cell': datumCell.subtotal }"
            mat-footer-cell
          >
            {{ tabulation!.columnTotals[datumCell.id] | rdsNumber: minFractionDigits:maxFractionDigits:numberFormat }}

            <span *ngIf="percentType === 'ROW' || percentType === 'BOTH'" class="percent-container percent-ROW">
              <div class="row-percent">
                {{
                  tabulation!.columnTotals[datumCell.id]
                    | rdsPctTotal: tabulation!.columnTotals['row-total']:minFractionDigits:maxFractionDigits
                }}
                <mat-icon class="percent-icon" fxLayout="row" fxLayoutAlign="center center">forward</mat-icon>
              </div>
            </span>
          </td>
        </ng-container>
      </ng-container>
    </ng-template>

    <!-- TableDimension Name Row -->
    <ng-container *ngIf="tableDim.headerIds.length">
      <tr class="column-names-row" mat-header-row *matHeaderRowDef="tableDim.headerIds; sticky: !platform.TRIDENT"></tr>
    </ng-container>

    <!-- Inner Column TableDimension Row -->
    <ng-container *ngIf="lastDim; else notLast">
      <tr class="row-names-column-values-row" mat-header-row *matHeaderRowDef="tabulation.tableHeaderDefs; sticky: !platform.TRIDENT"></tr>
    </ng-container>
    <!-- Outer Column TableDimension Row(s) -->
    <ng-template #notLast>
      <tr class="column-names-row" mat-header-row *matHeaderRowDef="tableDim | rdsTabCellIds; sticky: !platform.TRIDENT"></tr>
    </ng-template>
  </ng-container>

  <!-- Table cells (row values & column counts) -->
  <tr
    mat-row
    *matRowDef="let row; columns: tabulation.tableCellDefs; let even = even"
    [ngClass]="{ even: even, 'sub-total': row.subTotal }"
  ></tr>

  <tr mat-footer-row *matFooterRowDef="tabulation.tableCellDefs" [class.rds-c-footer-hide]="!tabulation.hasColumnTotals"></tr>
</table>

<ng-template #percentage let-cell="cell" let-rowTotal="rowTotal" let-columnTotal="columnTotal">
  <span class="percent-container" [ngClass]="'percent-' + percentType">
    <div class="row-percent">
      {{ cell | rdsPctTotal: rowTotal:minFractionDigits:maxFractionDigits }}
      <mat-icon class="percent-icon" fxLayout="row" fxLayoutAlign="center center">forward</mat-icon>
    </div>

    <div class="column-percent">
      {{ cell | rdsPctTotal: columnTotal:minFractionDigits:maxFractionDigits }}
      <mat-icon class="percent-icon rotate-90" fxLayout="row" fxLayoutAlign="center center">forward</mat-icon>
    </div>
  </span>
</ng-template>
